import { Cancel, Close, Save } from '@mui/icons-material';
import { Button, IconButton, Paper, Toolbar } from '@mui/material';
import { AppBar, Dialog, DialogContent, Stack, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next';
import EllipsisTypography from '../../../../components/EllipsisTypography';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import Loader from '../../../../components/Loader';
import TechInfo from '../../../../components/TechInfo';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import TabNavigation from '../../../../components/TabNavigation';
import { grey } from '@mui/material/colors';
import { updateQuestionnaireTemplateByKey } from '../../../../apis/questionnaireApis';
import { questionnaireTemplateDetailsActions, toastActions } from '../../../../_actions';
import QuestionnaireTemplateModal from './QuestionnaireTemplateModal';

const EditQuestionnaireTemplateDetailsDialog = ({ isOpenEditIconDialog, onClose }) => {
    const { t } = useTranslation();
    const theme = useTheme()
    const history = useHistory();
    const { questionnaireTemplateId  } = useParams()
    const dispatch = useDispatch()
    const { questionnaireStore: { questionnaireTemplateDetails: { templateDetails } } } = useSelector(state => state);
    const latestVersionNumber = Math.max(...Object.values(templateDetails).map(v => v.version));
    const currentTemplateDetails = templateDetails?.[latestVersionNumber] || null
    const isMobileOrTab = useMediaQuery(theme.breakpoints.down('md'));
    const [values, setValues] = useState(() => currentTemplateDetails)    
    const [errors, setErrors] = useState({})
    const [loading, setLoading] = useState(false)
    const [tabValue, setTabValue] = useState(["template"].includes(isOpenEditIconDialog?.tabId) ? 0 : 1);
    const [activeCheckboxState, setActiveCheckboxState] = useState({ isChecked: values?.active});
    
    const tabs = [
        { title: t("general.template") },
        { title: t("general.techInfo") },
    ];

    const handleTabChange = (_, newValue) => {
        setTabValue(newValue)
        history.push(tabs[newValue]?.to)
    };

    const handleChange = ({ keyName, value }) => {
        switch (keyName) {
            default:
                setValues({ ...values, [keyName]: value })
                break;
        }
    };

    const clearError = (field) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
        });
    };

    const validateSurveyTemplateDetails = () => {
        const newErrors = {};
        const { name, description ,reportHeader} = values || {};
        if (!name?.trim()) {
            newErrors.name = t("general.field.error");
        }

        if (!description?.trim()) {
            newErrors.description = t("general.field.error");
        }
         if (!reportHeader?.trim()) {
            newErrors.reportHeader = t("general.field.error");
        }
        setErrors((prevErrors) => ({
            ...prevErrors,
            ...newErrors,
        }));

        return { isValid: Object.keys(errors).length === 0, errors: { ...errors, ...newErrors } };
    };
    const updateTemplateDetails = async (data) => {
        try {
            setLoading(true)
            await updateQuestionnaireTemplateByKey(questionnaireTemplateId , data)
            dispatch(toastActions.success(t("template.update.success")))
        } catch (error) {
            dispatch(toastActions.error(t("general.failedToUpdate")))
        }
        finally {
            setLoading(false)
            onClose()
            dispatch(questionnaireTemplateDetailsActions.fetchQuestionnaireTemplateDetails(questionnaireTemplateId ))
        }
    }
    const getChangedFieldsArray = (original, updated,) => {
        const diff = [];
        Object.keys(updated).forEach((key) => {
            if (updated[key] !== original[key]) {
                diff.push({
                    id: key,
                    name: updated[key],
                });
            }
        });

        return diff;
    };

    const handleSave = () => {
        const validateFields = validateSurveyTemplateDetails();

        if (!validateFields.isValid || Object.keys(validateFields.errors).length > 0) {
            return;
        }
        const diffArray = getChangedFieldsArray(currentTemplateDetails, {
            ...values,
            active: activeCheckboxState.isChecked.toString(),
        });
        updateTemplateDetails(diffArray);
        
    };

    return (
        <Dialog
            open={isOpenEditIconDialog}
            PaperProps={{
                sx: {
                    maxWidth: "none",
                    width: 1200,
                    height: { md: 650 }
                }
            }}
            onClose={(event, reason) => reason === 'backdropClick' ? event.stopPropagation() : onClose} 
            fullScreen={isMobileOrTab}
        >
            <AppBar position='relative' sx={{ boxShadow: 'none' }} >
                <Toolbar >
                    <Stack direction={"row"} width={"100%"} alignItems={"center"} justifyContent={"space-between"}>
                        <Stack direction={"row"} alignItems={"center"}>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={() => onClose()}
                                aria-label="close"
                            >
                                <Close />
                            </IconButton>
                            <EllipsisTypography variant={"h6"} sx={{ ml: 2 }} component="div" title={t('general.template')} />
                        </Stack>
                        <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <Button onClick={() => onClose()} size='small' sx={{ display: { xs: 'none', sm: "inline-flex" } }} variant="text" color='inherit' startIcon={<Cancel />} >
                                {t("general.cancel")}
                            </Button>
                            <IconButton onClick={() => onClose()} sx={{ display: { sm: "none" } }} color='inherit'  >
                                <Cancel />
                            </IconButton>
                            <Button onClick={handleSave} sx={{ display: { xs: 'none', sm: "inline-flex" } }} size='small' variant="text" color='inherit' startIcon={<Save />}>
                                {t("general.save")}
                            </Button>
                            <IconButton onClick={handleSave} sx={{ display: { sm: "none" } }} color='inherit'  >
                                <Save />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>
            <TabNavigation tabs={tabs} activeTab={tabValue} handleTabChange={handleTabChange} />
            <DialogContent sx={{ bgcolor: grey[100], mt: 0.3, p: 2 }}>
                <Paper sx={{ width: "100%", bgcolor: "common.white" }} elevation={0}>
                    {loading && <Loader backdrop />}
                    {tabValue === 0 && <QuestionnaireTemplateModal value={values} errors={errors} activeCheckboxState={activeCheckboxState} setActiveCheckboxState={setActiveCheckboxState} handleChange={handleChange} clearError={clearError} />}
                    {tabValue === 1 && <TechInfo title={t('general.techInfo')} subtitle={t("general.techInfo")} data={values} />}
                </Paper>
            </DialogContent>
        </Dialog>
    )
}

export default EditQuestionnaireTemplateDetailsDialog
